import { useCallback, useMemo, useRef } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import { useQueryClient } from '@tanstack/react-query';
import { ObjectBrowser } from '@/components/ObjectBrowser';
import type { BrowserFetcherResult, BrowserFilterConfig } from '@/components/ObjectBrowser';
import type { MapBrowserObject } from '@/components/ObjectMapBrowser';
import { initiativesApi } from '@/lib/api';
import type { ProviderOpportunity } from '@/lib/api/types';
import { useUrlSync } from '@/lib/hooks/useUrlSync';
import {
  useInitiativeDetailSheet,
  buildInitiativeNavigationList,
} from '@/lib/hooks/useInitiativeDetailSheet';
import { DatePickerWithRange } from '@/components/custom/date-picker-with-range';
import type { DateRange } from 'react-day-picker';
import { Button } from '@/components/ui/button';
import { X, Expand } from 'lucide-react';
import { format } from 'date-fns';
import {
  OBJECT_COMPONENT_CATEGORY_OPTIONS,
  OBJECT_COMPONENT_COMPENSATION_OPTIONS,
  INITIATIVE_TYPE_CONFIG,
  RelativeDatePresents,
  formatEventDateRange,
} from '@sizzlenz/shared';

function opportunityToMapObject(opportunity: ProviderOpportunity): MapBrowserObject {
  const addressData = (opportunity.initiative.data as Record<string, any>)?.address_1;
  const lat = addressData?.geometry?.location?.lat;
  const lng = addressData?.geometry?.location?.lng;

  const categoryLabel =
    OBJECT_COMPONENT_CATEGORY_OPTIONS.find((o) => o.value === opportunity.component.category)
      ?.label || opportunity.component.category;

  return {
    id: `${opportunity.initiative.id}-${opportunity.component.id}`,
    objectType: 'initiative',
    lat: lat ?? undefined,
    lng: lng ?? undefined,
    title: opportunity.component.title,
    image:
      (opportunity.initiative as any).image ?? opportunity.initiative.owner?.image ?? undefined,
    href: `/initiatives/${opportunity.initiative.id}?source=asks`,
    tags: [categoryLabel, opportunity.initiative.name].filter(Boolean),
    data: opportunity,
  };
}

const FILTER_CONFIGS: BrowserFilterConfig[] = [
  {
    key: 'componentCategory',
    label: 'Category',
    options: OBJECT_COMPONENT_CATEGORY_OPTIONS.map(({ value, label }) => ({
      id: value,
      label,
    })),
    multi: false,
  },
  {
    key: 'componentCompensation',
    label: 'Compensation',
    options: OBJECT_COMPONENT_COMPENSATION_OPTIONS.map(({ value, label }) => ({
      id: value,
      label,
    })),
    multi: false,
  },
  {
    key: 'initiativeType',
    label: 'Initiative Type',
    options: Object.entries(INITIATIVE_TYPE_CONFIG)
      .filter(([key]) => key !== 'quiz')
      .map(([key, config]) => ({
        id: key,
        label: config.label,
      })),
  },
];

function ProviderOpportunityCard({
  opportunity,
  onExpand,
}: {
  opportunity: ProviderOpportunity;
  onExpand: () => void;
}) {
  const { initiative, component } = opportunity;
  const categoryLabel =
    OBJECT_COMPONENT_CATEGORY_OPTIONS.find((o) => o.value === component.category)?.label ||
    component.category;
  const compensationLabel =
    OBJECT_COMPONENT_COMPENSATION_OPTIONS.find((o) => o.value === component.compensation)?.label ||
    component.compensation;
  const dateStr = formatEventDateRange(initiative.startDate, initiative.endDate);
  return (
    <Link
      href={`/initiatives/${initiative.id}?source=asks`}
      className="relative flex flex-col h-full rounded-xl border bg-white p-3 hover:shadow-md transition-shadow cursor-pointer"
    >
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onExpand();
        }}
        className="absolute top-2 right-2 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-gray-100 transition-colors cursor-pointer"
        title="Quick view"
      >
        <Expand className="h-3.5 w-3.5" />
      </button>
      <h3 className="font-semibold text-sm leading-snug line-clamp-3 pr-6">{component.title}</h3>
      <p className="text-sm text-indigo-600 mt-1 leading-snug line-clamp-3">
        {initiative.name}
        {initiative.owner?.name ? ` · ${initiative.owner.name}` : ''}
      </p>
      {dateStr && <p className="text-xs text-muted-foreground mt-1">{dateStr}</p>}
      <div className="mt-auto pt-2 flex items-center gap-1.5 flex-wrap text-[10px]">
        <span className="px-1.5 py-px rounded bg-blue-50 text-blue-700">{categoryLabel}</span>
        {component.compensation !== 'flexible' && (
          <span className="px-1.5 py-px rounded bg-green-50 text-green-700">
            {compensationLabel}
          </span>
        )}
      </div>
    </Link>
  );
}

export default function AsksPage() {
  const queryClient = useQueryClient();
  const { showInitiativeDetail } = useInitiativeDetailSheet();
  const loadedOpportunitiesRef = useRef<ProviderOpportunity[]>([]);

  const { values, handlers, isReady } = useUrlSync({
    filters: {
      search: { paramName: 'search', defaultValue: '' },
      componentCategory: { paramName: 'category', defaultValue: [] as string[], isArray: true },
      componentCompensation: {
        paramName: 'compensation',
        defaultValue: [] as string[],
        isArray: true,
      },
      initiativeType: { paramName: 'initiativeType', defaultValue: [] as string[], isArray: true },
      dateFrom: { paramName: 'dateFrom', defaultValue: '' },
      dateTo: { paramName: 'dateTo', defaultValue: '' },
    },
  });

  const dateRange: DateRange | undefined =
    values.dateFrom || values.dateTo
      ? {
          from: values.dateFrom ? new Date(values.dateFrom) : undefined,
          to: values.dateTo ? new Date(values.dateTo) : undefined,
        }
      : undefined;

  const handleDateRangeChange = useCallback(
    (newDateRange: DateRange | undefined) => {
      handlers.dateFrom(newDateRange?.from?.toISOString() || '');
      handlers.dateTo(newDateRange?.to?.toISOString() || '');
    },
    [handlers.dateFrom, handlers.dateTo]
  );

  const whenDatePresets = useMemo(() => {
    return RelativeDatePresents.filter((p) => !p.id.startsWith('last') && p.id !== 'yesterday');
  }, []);

  const initialFilters = useMemo(() => {
    const f: Record<string, string[]> = {};
    if (values.componentCategory.length > 0) f.componentCategory = values.componentCategory;
    if (values.componentCompensation.length > 0)
      f.componentCompensation = values.componentCompensation;
    if (values.initiativeType.length > 0) f.initiativeType = values.initiativeType;
    return f;
  }, [
    values.componentCategory.join(','),
    values.componentCompensation.join(','),
    values.initiativeType.join(','),
  ]);

  const fetcher = useCallback(
    async (params: {
      search?: string;
      filters: Record<string, string[]>;
      cursor?: string;
      limit: number;
    }): Promise<BrowserFetcherResult<ProviderOpportunity>> => {
      const initiativeTypes = params.filters.initiativeType || [];

      const query = {
        search: params.search || undefined,
        componentCategory: params.filters.componentCategory?.[0] || undefined,
        componentCompensation: params.filters.componentCompensation?.[0] || undefined,
        componentStatus: 'open',
        initiativeType: initiativeTypes.length > 0 ? initiativeTypes : undefined,
        dateFrom: values.dateFrom || undefined,
        dateTo: values.dateTo || undefined,
        limit: params.limit,
        nextCursor: params.cursor || undefined,
      };

      const response = await queryClient.fetchQuery({
        queryKey: ['provider-opportunities', query],
        queryFn: () => initiativesApi.getProviderOpportunities(query),
        staleTime: 1000 * 10,
      });

      // Track loaded objects for navigation list building
      if (!params.cursor) {
        loadedOpportunitiesRef.current = response.opportunities;
      } else {
        loadedOpportunitiesRef.current = [
          ...loadedOpportunitiesRef.current,
          ...response.opportunities,
        ];
      }

      return {
        objects: response.opportunities,
        hasNextPage: !!response.pagination.nextCursor,
        nextCursor: response.pagination.nextCursor ?? undefined,
      };
    },
    [queryClient, values.dateFrom, values.dateTo]
  );

  const handleSearchChange = useCallback(
    (search: string) => handlers.search(search),
    [handlers.search]
  );

  const handleFiltersChange = useCallback(
    (filters: Record<string, string[]>) => {
      handlers.componentCategory(filters.componentCategory || []);
      handlers.componentCompensation(filters.componentCompensation || []);
      handlers.initiativeType(filters.initiativeType || []);
    },
    [handlers.componentCategory, handlers.componentCompensation, handlers.initiativeType]
  );

  /** Build a deduplicated initiative navigation list from loaded opportunities */
  const getNavigationList = useCallback((opportunities: ProviderOpportunity[]) => {
    const seen = new Set<number>();
    const uniqueInitiatives = opportunities
      .map((o) => o.initiative)
      .filter((i) => {
        if (seen.has(i.id)) return false;
        seen.add(i.id);
        return true;
      });
    return uniqueInitiatives.length > 1
      ? buildInitiativeNavigationList(uniqueInitiatives)
      : undefined;
  }, []);

  const handleShowDetail = useCallback(
    (obj: MapBrowserObject, allObjects?: MapBrowserObject[]) => {
      const opportunity = obj.data as ProviderOpportunity;
      const source = allObjects
        ? allObjects
            .filter((o) => o.objectType === 'initiative')
            .map((o) => o.data as ProviderOpportunity)
        : loadedOpportunitiesRef.current;
      const navigationList = getNavigationList(source);
      showInitiativeDetail(opportunity.initiative, { navigationList });
    },
    [showInitiativeDetail, getNavigationList]
  );

  const dateChip = useMemo(() => {
    if (!dateRange?.from && !dateRange?.to) return null;
    const fromStr = dateRange?.from ? format(dateRange.from, 'MMM d, yyyy') : '';
    const toStr = dateRange?.to ? format(dateRange.to, 'MMM d, yyyy') : '';
    const label = fromStr && toStr ? `${fromStr} – ${toStr}` : fromStr || toStr;
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary-50 text-primary-700 rounded-full text-xs font-medium">
        {label}
        <button
          onClick={() => handleDateRangeChange(undefined)}
          className="hover:text-primary-900 cursor-pointer"
        >
          <X className="w-3 h-3" />
        </button>
      </span>
    );
  }, [dateRange, handleDateRangeChange]);

  if (!isReady) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <>
      <Head>
        <title>Asks | Sizzlenz</title>
        <meta
          name="description"
          content="Browse opportunities to provide venues, food, beverages and more for initiatives."
        />
      </Head>
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <div className="mx-auto px-3 sm:px-6 lg:px-8 py-2 sm:py-2">
          <div className="mb-4">
            <h1 className="text-2xl font-bold text-gray-900">Asks</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Initiatives looking for providers — venues, food, beverages and more.
            </p>
          </div>

          <ObjectBrowser<ProviderOpportunity>
            fetcher={fetcher}
            fetchKey={`${values.dateFrom}|${values.dateTo}`}
            filterSlot={
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">When</label>
                <div className="flex flex-wrap items-center gap-2">
                  <DatePickerWithRange
                    presets={whenDatePresets}
                    date={dateRange}
                    setDate={handleDateRangeChange}
                    clearable={true}
                  />
                  {dateRange && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0 cursor-pointer"
                      onClick={() => handleDateRangeChange(undefined)}
                      title="Clear date range"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            }
            renderCard={(opportunity) => (
              <ProviderOpportunityCard
                opportunity={opportunity}
                onExpand={() => {
                  const navigationList = getNavigationList(loadedOpportunitiesRef.current);
                  showInitiativeDetail(opportunity.initiative, { navigationList });
                }}
              />
            )}
            getKey={(opportunity) => `${opportunity.initiative.id}-${opportunity.component.id}`}
            filters={FILTER_CONFIGS}
            searchEnabled
            searchPlaceholder="Search..."
            limit={20}
            emptyState={{
              title: 'No matching active Asks',
              description: 'Try adjusting your search, filters, or date range',
            }}
            initialSearch={values.search}
            initialFilters={initialFilters}
            onSearchChange={handleSearchChange}
            onFiltersChange={handleFiltersChange}
            filterChipSlot={dateChip}
            toMapObject={opportunityToMapObject}
            mapConfig={{
              defaultShowMap: true,
              showMapOnEmpty: true,
              onShowDetail: handleShowDetail,
            }}
          />
        </div>
      </div>
    </>
  );
}
